import { alpha, createTheme } from "@mui/material";

// Accss design system v2 - same visual language as the accessibility widget (embed.js v2):
// slate neutrals, one blue accent, soft bordered cards, pill controls, generous radii.
// Shared tokens are exported so pages/components never hard-code colors.

const rem = (px) => `${px / 16}rem`;

export const AW_BRAND = {
  primary: "#1976d2", // same default accent as the widget
  primaryDark: "#1565c0",
  secondary: "#0f766e",
};

const STATUS = {
  success: "#16a34a",
  warning: "#d97706",
  error: "#dc2626",
  info: "#2563eb",
};

export const AW_TOKENS = {
  light: {
    bg: "#f8fafc",
    paper: "#ffffff",
    soft: "#f1f5f9", // segmented controls, hover rows, chips
    softer: "#f8fafc",
    border: "#e2e8f0",
    borderStrong: "#cbd5e1",
    text: "#0f172a",
    text2: "#475569",
    muted: "#64748b",
    faint: "#94a3b8",
    accentSoft: "#e8f1fc", // selected nav item / active card background
    shadowSm: "0 1px 2px rgba(15,23,42,0.04), 0 1px 3px rgba(15,23,42,0.06)",
    shadowMd: "0 4px 6px -1px rgba(15,23,42,0.06), 0 10px 24px -6px rgba(15,23,42,0.10)",
    shadowLg: "0 24px 48px -12px rgba(15,23,42,0.18)",
  },
  dark: {
    bg: "#0b1220",
    paper: "#111a2e",
    soft: "#17223a",
    softer: "#0f182b",
    border: "rgba(148,163,184,0.16)",
    borderStrong: "rgba(148,163,184,0.28)",
    text: "#e5e7eb",
    text2: "#cbd5e1",
    muted: "#94a3b8",
    faint: "#64748b",
    accentSoft: "rgba(25,118,210,0.18)",
    shadowSm: "0 1px 2px rgba(0,0,0,0.3)",
    shadowMd: "0 8px 24px -8px rgba(0,0,0,0.5)",
    shadowLg: "0 24px 48px -12px rgba(0,0,0,0.6)",
  },
};

export const AW_RADIUS = { sm: 10, md: 12, lg: 16, xl: 20, pill: 999 };

export const AW_FONT = "Rubik, Inter, system-ui, -apple-system, 'Segoe UI', Arial, sans-serif";

export function createAwTheme({ direction = "rtl", mode = "light" } = {}) {
  const t = mode === "dark" ? AW_TOKENS.dark : AW_TOKENS.light;

  return createTheme({
    direction,
    palette: {
      mode,
      primary: { main: AW_BRAND.primary, dark: AW_BRAND.primaryDark, contrastText: "#fff" },
      secondary: { main: AW_BRAND.secondary },
      success: { main: STATUS.success },
      warning: { main: STATUS.warning },
      error: { main: STATUS.error },
      info: { main: STATUS.info },
      background: { default: t.bg, paper: t.paper },
      text: { primary: t.text, secondary: t.muted },
      divider: t.border,
      action: { hover: t.soft, selected: t.accentSoft },
      grey: { 50: t.softer, 100: t.soft },
    },
    // Custom tokens available as theme.aw.* in sx callbacks
    aw: { ...t, radius: AW_RADIUS },
    shape: { borderRadius: AW_RADIUS.md },
    typography: {
      fontFamily: AW_FONT,
      // rem so the widget's text scaling (html font-size) also scales the app
      h1: { fontSize: rem(52), fontWeight: 800, letterSpacing: "-0.025em", lineHeight: 1.1 },
      h2: { fontSize: rem(36), fontWeight: 800, letterSpacing: "-0.02em", lineHeight: 1.15 },
      h3: { fontSize: rem(28), fontWeight: 700, letterSpacing: "-0.01em", lineHeight: 1.2 },
      h4: { fontSize: rem(22), fontWeight: 700, lineHeight: 1.25 },
      h5: { fontSize: rem(18), fontWeight: 700, lineHeight: 1.3 },
      h6: { fontSize: rem(16), fontWeight: 700, lineHeight: 1.35 },
      subtitle1: { fontSize: rem(15), fontWeight: 600 },
      subtitle2: { fontSize: rem(13), fontWeight: 600, color: t.muted },
      body1: { fontSize: rem(15), lineHeight: 1.6 },
      body2: { fontSize: rem(14), lineHeight: 1.55 },
      caption: { fontSize: rem(12), color: t.muted },
      overline: { fontSize: rem(11), fontWeight: 700, letterSpacing: "0.08em", color: t.muted },
      button: { fontWeight: 600, textTransform: "none" },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: { backgroundColor: t.bg, WebkitFontSmoothing: "antialiased" },
          "::selection": { background: alpha(AW_BRAND.primary, 0.18) },
        },
      },
      MuiContainer: { defaultProps: { maxWidth: "lg" } },
      MuiAppBar: {
        defaultProps: { elevation: 0, color: "inherit" },
        styleOverrides: {
          root: { backgroundColor: alpha(t.paper, 0.85), backdropFilter: "saturate(180%) blur(12px)", borderBottom: `1px solid ${t.border}` },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: AW_RADIUS.md,
            fontWeight: 600,
            paddingInline: 16,
            minHeight: 40,
            "&:focus-visible": { outline: `3px solid ${alpha(AW_BRAND.primary, 0.4)}`, outlineOffset: 2 },
          },
          sizeLarge: { minHeight: 48, paddingInline: 22, fontSize: rem(15), borderRadius: AW_RADIUS.md },
          sizeSmall: { minHeight: 32, paddingInline: 12, borderRadius: AW_RADIUS.sm },
          containedPrimary: { "&:hover": { backgroundColor: AW_BRAND.primaryDark } },
          outlined: { borderColor: t.border, backgroundColor: t.paper, color: t.text, "&:hover": { borderColor: t.borderStrong, backgroundColor: t.soft } },
          text: { "&:hover": { backgroundColor: t.soft } },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: { borderRadius: AW_RADIUS.sm, "&:hover": { backgroundColor: t.soft } },
        },
      },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: { borderRadius: AW_RADIUS.lg, border: `1px solid ${t.border}`, boxShadow: t.shadowSm, backgroundImage: "none" },
        },
      },
      MuiCardContent: {
        styleOverrides: { root: { padding: 24, "&:last-child": { paddingBottom: 24 } } },
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: "none" },
          rounded: { borderRadius: AW_RADIUS.lg },
          outlined: { borderColor: t.border },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: { borderRadius: AW_RADIUS.xl, boxShadow: t.shadowLg, border: `1px solid ${t.border}` },
        },
      },
      MuiDialogTitle: { styleOverrides: { root: { fontWeight: 700, fontSize: rem(18) } } },
      MuiMenu: {
        styleOverrides: { paper: { borderRadius: AW_RADIUS.md, boxShadow: t.shadowMd, border: `1px solid ${t.border}` } },
      },
      MuiMenuItem: {
        styleOverrides: { root: { borderRadius: 8, marginInline: 6, minHeight: 38 } },
      },
      MuiTextField: { defaultProps: { variant: "outlined", size: "medium" } },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: t.paper,
            borderRadius: AW_RADIUS.md,
            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: t.borderStrong },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderWidth: 2 },
          },
          notchedOutline: { borderColor: t.border },
        },
      },
      MuiInputLabel: { styleOverrides: { root: { color: t.muted } } },
      MuiFormHelperText: { styleOverrides: { root: { marginInline: 4, color: t.muted } } },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: AW_RADIUS.pill, fontWeight: 600 },
          sizeSmall: { height: 24, fontSize: rem(12) },
          outlined: { borderColor: t.border },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: AW_RADIUS.md, border: "1px solid", alignItems: "center" },
          standardInfo: { borderColor: alpha(STATUS.info, 0.2), backgroundColor: alpha(STATUS.info, 0.06) },
          standardSuccess: { borderColor: alpha(STATUS.success, 0.25), backgroundColor: alpha(STATUS.success, 0.07) },
          standardWarning: { borderColor: alpha(STATUS.warning, 0.3), backgroundColor: alpha(STATUS.warning, 0.08) },
          standardError: { borderColor: alpha(STATUS.error, 0.25), backgroundColor: alpha(STATUS.error, 0.06) },
        },
      },
      // Segmented-control look (like the widget's tab bar)
      MuiTabs: {
        styleOverrides: {
          root: { minHeight: 44, backgroundColor: t.soft, borderRadius: AW_RADIUS.md, padding: 4 },
          indicator: { display: "none" },
          flexContainer: { gap: 4 },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            minHeight: 36,
            borderRadius: 9,
            textTransform: "none",
            fontWeight: 600,
            color: t.muted,
            paddingInline: 16,
            "&.Mui-selected": { color: AW_BRAND.primary, backgroundColor: t.paper, boxShadow: t.shadowSm },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderColor: t.border },
          head: { fontWeight: 600, fontSize: rem(12), color: t.muted, backgroundColor: t.softer, letterSpacing: "0.02em" },
        },
      },
      MuiTableRow: {
        styleOverrides: { root: { "&.MuiTableRow-hover:hover": { backgroundColor: t.softer } } },
      },
      MuiLinearProgress: {
        styleOverrides: { root: { borderRadius: AW_RADIUS.pill, height: 8, backgroundColor: t.soft }, bar: { borderRadius: AW_RADIUS.pill } },
      },
      MuiSwitch: {
        styleOverrides: {
          switchBase: { "&.Mui-checked + .MuiSwitch-track": { opacity: 1 } },
          track: { borderRadius: AW_RADIUS.pill, backgroundColor: t.borderStrong, opacity: 1 },
        },
      },
      MuiDivider: { styleOverrides: { root: { borderColor: t.border } } },
      MuiTooltip: {
        styleOverrides: { tooltip: { borderRadius: 8, fontSize: rem(12), backgroundColor: "#0f172a" } },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: AW_RADIUS.md,
            "&.Mui-selected": { backgroundColor: t.accentSoft, color: AW_BRAND.primary },
            "&.Mui-selected:hover": { backgroundColor: t.accentSoft },
          },
        },
      },
      MuiStepIcon: { styleOverrides: { root: { "&.Mui-active, &.Mui-completed": { color: AW_BRAND.primary } } } },
    },
  });
}
